import { describeCronExpression } from './dashboard-cron-utils.js';

/**
 * Built-in list filters for the `filtered` dashboard widget type.
 *
 * Each filter receives the raw API response and returns an array of
 * { key: string, value: string } objects that the metric-filtered
 * component renders as a two-column table.
 *
 * Widget config examples:
 *
 * Root only (legacy):
 *   { "type": "filtered", "url": "/admin/workflows", "filter": "humanReadableSchedule", "label": "Process" }
 *
 * Sources only:
 *   { "type": "filtered", "sources": [{ "url": "...", "filter": "...", "label": "A" }] }
 *
 * Root + sources (merged — root group first, then each source):
 *   {
 *     "type": "filtered",
 *     "url": "/admin/workflows",
 *     "params": { "query": "key:a" },
 *     "filter": "humanReadableSchedule",
 *     "label": "Primary",
 *     "sources": [{ "url": "/admin/items", "filter": "humanReadableSchedule", "label": "Secondary" }]
 *   }
 *
 * Both root and `sources` are optional. At least one valid source (url + filter) is required to fetch.
 *
 * `sourceField` is an optional dot-path to the source field on each record (default: scheduled.timer).
 */

function isValidFilteredSource(source) {
  return Boolean(source?.url && source?.filter);
}

function resolveRootSource(widget) {
  if (!widget?.url || !widget?.filter) {
    return null;
  }

  return {
    url: widget.url,
    params: widget.params,
    filter: widget.filter,
    sourceField: widget.sourceField,
    label: widget.label,
  };
}

/**
 * Normalize a filtered widget config to an array of source definitions.
 * Merges optional root-level url/params/filter with optional `sources` array (root first).
 * Invalid source entries (missing url or filter) are skipped with a console warning.
 *
 * @param {any} widget
 * @returns {Array<{ url: string, params?: object, filter: string, sourceField?: string, label?: string }>}
 */
export function resolveFilteredSources(widget) {
  const merged = [];

  const root = resolveRootSource(widget);
  if (root) {
    merged.push(root);
  }

  if (Array.isArray(widget?.sources)) {
    widget.sources.forEach((source, index) => {
      if (isValidFilteredSource(source)) {
        merged.push(source);
      } else {
        const missing = [!source?.url && 'url', !source?.filter && 'filter'].filter(Boolean).join(', ');
        console.warn(`[filtered] sources[${index}] is missing required field(s): ${missing}. This source will be skipped.`, source);
      }
    });
  }

  return merged;
}

/**
 * Read a nested field from an object using dot notation (e.g. "scheduled.timer").
 *
 * @param {any} object
 * @param {string} path
 * @returns {any}
 */
export function resolveFieldValue(object, path) {
  if (!path || object === null || object === undefined) return undefined;
  return path.split('.').reduce((current, key) => current?.[key], object);
}

/**
 * Resolve the items array from common Helix API response shapes.
 *
 * Handles:
 * - { records: [...] }  (Helix admin list response)
 * - { hits: [...] }
 * - { data: [...] }
 * - { items: [...] }
 * - { results: [...] }
 *
 * @param {any} response
 * @returns {any[]}
 */
function resolveHits(response) {
  if (Array.isArray(response)) return response;
  return response?.records ?? response?.hits ?? response?.data ?? response?.items ?? response?.results ?? [];
}

/**
 * humanReadableSchedule — record key + a cron field converted to human-readable text.
 *
 * @param {any} response
 * @param {{ locale?: string, sourceField?: string }} [options]
 * @returns {{ key: string, value: string }[]}
 */
export function humanReadableScheduleFilter(response, options = {}) {
  const sourceField = options.sourceField ?? 'scheduled.timer';

  return resolveHits(response).map((item) => ({
    key: item.key ?? item._key ?? item.id ?? '—',
    value: describeCronExpression(resolveFieldValue(item, sourceField), options.locale),
  }));
}

/**
 * Built-in filter registry. The `filter` field in the widget config references
 * one of these keys.
 *
 * @type {Record<string, (response: any, options?: { locale?: string, sourceField?: string }) => { key: string, value: string }[]>}
 */
export const listFilters = {
  humanReadableSchedule: humanReadableScheduleFilter,
};

/**
 * Apply a named filter to a raw API response.
 *
 * @param {string} filterName
 * @param {any} response
 * @param {{ locale?: string, sourceField?: string }} [options]
 * @returns {{ key: string, value: string }[]}
 */
export function applyListFilter(filterName, response, options = {}) {
  if (!filterName) return [];
  const fn = listFilters[filterName];
  if (!fn) {
    const available = Object.keys(listFilters).join(', ');
    const message = `[filtered] Unknown filter: "${filterName}". Available: ${available}`;
    console.warn(message);
    throw new Error(message);
  }
  return fn(response, options);
}
